import { Loader2, Sparkles, TriangleAlert, Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

/*
 * AI pedigree review screen — the heart of "AI proposes, organizer validates".
 * Every extracted value is editable; undetected values stay empty and flagged.
 * Nothing is persisted here: Apply only merges into the horse form.
 */

const PATHS = [
  ["sire"], ["dam"],
  ["sire", "sire"], ["sire", "dam"], ["dam", "sire"], ["dam", "dam"],
  ["sire", "sire", "sire"], ["sire", "sire", "dam"], ["sire", "dam", "sire"], ["sire", "dam", "dam"],
  ["dam", "sire", "sire"], ["dam", "sire", "dam"], ["dam", "dam", "sire"], ["dam", "dam", "dam"],
];

const getNode = (tree, path) => path.reduce((n, k) => (n ? n[k] : undefined), tree);

const setNodeName = (tree, path, name) => {
  const next = JSON.parse(JSON.stringify(tree || { name: "—" }));
  let cur = next;
  for (const key of path.slice(0, -1)) {
    if (!cur[key]) cur[key] = { name: "" };
    cur = cur[key];
  }
  const leaf = path[path.length - 1];
  if (!cur[leaf]) cur[leaf] = { name: "" };
  cur[leaf].name = name;
  return next;
};

const FIELD_KEYS = ["name", "year_of_birth", "breed", "studbook", "breeder", "country", "color"];

export const PedigreeReviewDialog = ({ open, onOpenChange, result, onApply }) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState({});
  const [regIds, setRegIds] = useState({});
  const [tree, setTree] = useState(null);
  const [description, setDescription] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!result) return;
    const h = result.horse || {};
    setFields(Object.fromEntries(FIELD_KEYS.map((k) => [k, h[k] ?? ""])));
    setRegIds(h.registration_ids || {});
    setTree(result.pedigree || null);
    setDescription("");
  }, [result]);

  const generateDescription = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/horses/describe", { ...fields, pedigree: tree }, { timeout: 90000 });
      setDescription(data.description);
    } catch {
      setDescription("");
    } finally {
      setGenerating(false);
    }
  };

  if (!result) return null;
  const notes = result.notes || [];
  const gens = [
    { label: t("ped.gen1"), paths: PATHS.slice(0, 2) },
    { label: t("ped.gen2"), paths: PATHS.slice(2, 6) },
    { label: t("ped.gen3"), paths: PATHS.slice(6) },
  ];

  const apply = () => {
    onApply({
      fields: { ...fields, year_of_birth: fields.year_of_birth ? Number(fields.year_of_birth) : "" },
      registration_ids: regIds,
      pedigree: tree,
      description: description.trim() || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white rounded-2xl max-w-3xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto p-6 sm:p-8"
        data-testid="pedigree-review-dialog"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 tracking-tight">
            <Sparkles className="w-5 h-5 text-[#1D4D3B]" aria-hidden="true" />
            {t("ai.reviewTitle")}
          </DialogTitle>
          <DialogDescription>{t("ai.reviewSubtitle")}</DialogDescription>
        </DialogHeader>

        {notes.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3" data-testid="ai-notes">
            {notes.map((n, i) => (
              <span key={i} className="inline-flex items-start gap-2 text-xs text-amber-800">
                <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                {n}
              </span>
            ))}
          </div>
        )}

        {/* identity fields */}
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/50">{t("ai.fields")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {FIELD_KEYS.map((k) => (
              <div key={k} className="flex flex-col gap-1">
                <Label htmlFor={`ai-f-${k}`} className="text-xs text-[#737373]">
                  {t(`ai.f.${k}`)}
                </Label>
                <Input
                  id={`ai-f-${k}`}
                  data-testid={`ai-field-${k}`}
                  value={fields[k] ?? ""}
                  placeholder={t("ai.notDetected")}
                  onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                  className={`h-10 rounded-xl border-black/10 ${fields[k] ? "" : "border-amber-200 bg-amber-50/40"}`}
                />
              </div>
            ))}
          </div>
          {Object.keys(regIds).length > 0 && (
            <div className="flex flex-col gap-2 pt-1">
              <span className="text-xs text-[#737373]">{t("ai.regIds")}</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(regIds).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#0A0A0A]/60 w-20 shrink-0 truncate">{key}</span>
                    <Input
                      data-testid={`ai-regid-${key}`}
                      value={val ?? ""}
                      onChange={(e) => setRegIds((r) => ({ ...r, [key]: e.target.value }))}
                      className="h-9 rounded-lg border-black/10 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* pedigree tree — editable, horizontally scrollable, structure preserved */}
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/50">{t("ai.tree")}</h3>
          <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0 pb-1">
            <div className="flex gap-4 min-w-[720px]">
              {gens.map((gen, gi) => (
                <div key={gi} className="flex-1 flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/40">{gen.label}</span>
                  <div className="flex-1 flex flex-col justify-around gap-2">
                    {gen.paths.map((path) => {
                      const n = getNode(tree, path);
                      const label = path[path.length - 1] === "sire" ? t("pub.sire") : t("pub.dam");
                      return (
                        <div key={path.join(".")} className="flex flex-col gap-0.5">
                          <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#1D4D3B]">{label}</span>
                          <Input
                            data-testid={`ai-ped-${path.join("-")}`}
                            value={n?.name || ""}
                            placeholder={t("ai.notDetected")}
                            onChange={(e) => setTree((prev) => setNodeName(prev, path, e.target.value))}
                            className={`h-9 rounded-lg border-black/10 text-sm ${n?.name ? "" : "border-amber-200 bg-amber-50/40"}`}
                          />
                          {(n?.studbook || n?.year) && (
                            <span className="text-[10px] text-[#737373] pl-1">{[n.studbook, n.year].filter(Boolean).join(" · ")}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* one-click sale description drafted from the bloodline — organizer edits, never auto-saved */}
        <section className="flex flex-col gap-3">
          {description ? (
            <>
              <Label htmlFor="ai-description" className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/50">
                {t("ai.descriptionLabel")}
              </Label>
              <Textarea
                id="ai-description"
                data-testid="ai-description-textarea"
                value={description}
                rows={5}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-xl border-black/10"
              />
            </>
          ) : (
            <Button
              type="button"
              variant="ghost"
              data-testid="ai-generate-description"
              onClick={generateDescription}
              disabled={generating}
              className="w-fit rounded-full h-10 px-5 bg-[#1D4D3B]/[0.06] text-[#1D4D3B] hover:bg-[#1D4D3B]/10 transition-colors duration-300 text-sm"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                  {t("ai.generatingDescription")}
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" aria-hidden="true" />
                  {t("ai.generateDescription")}
                </>
              )}
            </Button>
          )}
        </section>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="ai-review-cancel" className="rounded-full h-11 px-6">
            {t("horse.cancel")}
          </Button>
          <Button
            type="button"
            onClick={apply}
            data-testid="ai-review-apply"
            className="rounded-full h-11 px-7 bg-[#0A0A0A] text-white hover:bg-black/80 transition-colors duration-300"
          >
            {t("ai.apply")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
